"use client";

import React, { useRef, useEffect, useState } from "react";

import Page from "@/components/ui/page";
import { tinaFieldSafe, useTina } from "@/utils";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
import Section from "@/components/ui/section";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import FAQs from "@/components/FAQs";
import TiptapSimpleContent from "@/components/TiptapSimpleContent";

export default function Features(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const header = data?.features?.header;
  const featureSections = data?.features?.sections;
  const faqsSection = data?.features?.faqs;
  const callToAction = data?.features?.cta;

  return (
    <Page
      className="relative"
      defaultTinaField={data?.features}
      seo={data?.features?.seo}
      pagePath="/features"
      titleFallback="Features"
      faqs={faqsSection}
    >
      <Header {...header} variant="left" />
      <FeatureSections featureSections={featureSections} />
      <FAQsSection faqsSection={faqsSection} />
      <CallToAction
        {...callToAction}
        variant="left"
        backgroundVariant="dots"
      />
    </Page>
  );
}

function FeatureSections({ featureSections }: { featureSections: any }) {
  return (
    <div className="">
      {featureSections?.map((featureSection: any, index: number) => (
        <FeaturesSection
          key={index}
          index={index}
          section={featureSection as any}
        />
      ))}
    </div>
  );
}

function FeaturesSection({ index, section }: { index: number; section: any }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [stickyTopValue, setStickyTopValue] = useState<number>(0);

  useEffect(() => {
    const calculateStickyBehavior = () => {
      if (!sectionRef.current) return;
      const sectionHeight = sectionRef.current.scrollHeight;
      const viewportHeight = window.innerHeight;
      if (sectionHeight > viewportHeight) {
        // Section is taller than viewport
        // Use negative top value so content scrolls naturally first, then sticks
        const difference = sectionHeight - viewportHeight;
        setStickyTopValue(-difference);
      } else {
        // Section fits in viewport, use normal sticky behavior at top
        setStickyTopValue(64);
      }
    };

    calculateStickyBehavior();
    window.addEventListener("resize", calculateStickyBehavior);

    return () => {
      window.removeEventListener("resize", calculateStickyBehavior);
    };
  }, [section]);

  return (
    <div
      ref={sectionRef}
      className="bg-background border-t border-border first:border-t-0 first:[&>section]:!pt-12 lg:sticky last:border-b"
      style={{
        top: `${stickyTopValue}px`,
      }}
    >
      <Section className="space-y-8 lg:space-y-16">
        <h2 className="h4" data-tina-field={tinaFieldSafe(section, "title")}>
          <span className="font-mono text-primary-200">0{index + 1}.</span>{" "}
          {section.title}
        </h2>
        <div className="space-y-8 lg:space-y-12">
          {section.features.map((feature: any, index: number) => {
            const featureImageSrc =
              typeof feature?.image === "string"
                ? feature.image.trim()
                : "";
            return (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-2 items-start gap-8 lg:gap-24 overflow-auto pb-12 max-md:!border-b border-border last:border-b-0"
              >
                <div className="space-y-3">
                  <h3
                    className="h4 text-balance mt-0.5 mx-0.5"
                    data-tina-field={tinaFieldSafe(feature, "title")}
                  >
                    {feature.title}
                  </h3>
                  <TiptapSimpleContent
                    content={feature.description}
                    className="p [&_p]:!text-muted-foreground-lighter"
                    data-tina-field={tinaFieldSafe(feature, "description")}
                  />
                </div>
                <div
                  data-tina-field={tinaFieldSafe(feature, "image")}
                  className="m-0.5"
                >
                  <Card wrapperClassName="!py-0">
                    <CardContent className="pt-8 pl-8 pr-0 pb-0 cursor-default">
                      {featureImageSrc ? (
                        <Image
                          src={featureImageSrc}
                          alt={`${feature.title} image`}
                          width={556}
                          height={313}
                          className="object-cover h-full w-full cursor-default"
                          quality={100}
                          sizes="(max-width: 768px) 100vw, 556px"
                        />
                      ) : (
                        <div
                          className="aspect-video w-full min-h-[176px] bg-gray-900"
                          aria-hidden
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

function FAQsSection({ faqsSection }: { faqsSection: any }) {
  if (!faqsSection?.showFaqs) {
    return null;
  }
  return (
    <Section backgroundVariant="none" className="space-y-8">
      <h2
        className="h1 text-center"
        data-tina-field={tinaFieldSafe(faqsSection, "title")}
      >
        {faqsSection?.title}
      </h2>
      <FAQs faqs={faqsSection?.faqs} />
    </Section>
  );
}
